(function (smartcart) {
  "use strict";

  var CART_MODAL_ID = "cart-modal";
  var CART_MODAL_SELECTOR = "#cart-modal";
  var CART_COUNT_SELECTOR = ".js-cart-count";
  var CART_TRIGGER_SELECTOR = ".cart-trigger";
  var OPT_OUT_SELECTOR = ".disable-rebuy, [data-rebuy-no-intercept]";
  var NATIVE_OPEN_CLASSES = ["modal--active", "modal--before-show"];
  var WAIT_TIMEOUT = 15000;

  // Survives across invocations; both contexts share it, which is what makes running the
  // theme asset and the admin callback together harmless.
  var STATE = (window.__REBUY_NATIVE_CART__ =
    window.__REBUY_NATIVE_CART__ || {});

  // Truthy only when Rebuy invoked us as the Smart Cart Ready callback.
  var IN_READY_CALLBACK = !!smartcart;

  // True only when Smart Cart is loaded, enabled and able to render. Re-checked at call time by
  // every deferred handler, so a later Rebuy failure always falls back to the native drawer.
  function smartCartReady() {
    return !!(
      window.Rebuy &&
      window.Rebuy.SmartCart &&
      window.Rebuy.SmartCart.status === "ready" &&
      typeof window.Rebuy.SmartCart.show === "function"
    );
  }

  // Opens Smart Cart while respecting Rebuy's own skip_open flag, which Rebuy sets during flows
  // that must NOT surface the cart (Bundle Builder add-to-checkout, SmartLink adds) and clears
  // after. onsite-js gates its own auto-show behind the same check.
  function showSmartCart() {
    if (window.Rebuy.SmartCart.skip_open) return;
    window.Rebuy.SmartCart.show();
  }

  // Returns the theme's Modal instance for #cart-modal, or null before app.js has built it.
  function getCartModal() {
    var manager = window.modalManager;
    if (
      !manager ||
      !manager.idModalMap ||
      typeof manager.idModalMap.get !== "function"
    )
      return null;
    return manager.idModalMap.get(CART_MODAL_ID) || null;
  }

  function funnelReady() {
    var modal = getCartModal();
    return !!(modal && typeof modal.show === "function");
  }

  // Wraps the cart Modal instance's own show() so it defers to Smart Cart. The native show is
  // retained and re-invoked whenever Smart Cart is not ready at click time, so the shopper is
  // never left without a cart UI.
  //
  // The already-patched test is `modal.rebuyNativeShow` on the INSTANCE rather than a flag in
  // this scope: that survives re-invocation, and still re-patches correctly if the theme ever
  // rebuilds the modal, since a fresh instance carries no marker.
  function patchCartModal() {
    if (!smartCartReady() || !funnelReady()) return;

    var modal = getCartModal();
    if (modal.rebuyNativeShow) return;

    var nativeShow = modal.show.bind(modal);

    modal.rebuyNativeShow = nativeShow;
    modal.show = function () {
      if (!smartCartReady()) return nativeShow();
      showSmartCart();
      return modal;
    };
  }

  // Waits for app.js to construct window.modalManager, then patches. Backs off up to 15s and
  // then gives up quietly, leaving the native drawer intact. Only one poll loop may be in
  // flight at a time, or a re-init would start a second one alongside the first.
  function waitForFunnelThenPatch() {
    if (funnelReady()) {
      patchCartModal();
      return;
    }
    if (STATE.polling) return;
    STATE.polling = true;

    var delay = 50;
    var elapsed = 0;

    (function poll() {
      if (funnelReady()) {
        STATE.polling = false;
        patchCartModal();
        return;
      }
      if (elapsed > WAIT_TIMEOUT) {
        STATE.polling = false;
        return;
      }
      elapsed += delay;
      setTimeout(poll, delay);
      delay = Math.min(delay * 2, 1000);
    })();
  }

  // Safety net for any path that opens the drawer by flipping classes directly instead of going
  // through Modal.show() — strips the open classes, closes the native modal, shows Smart Cart.
  // Re-observes only if the drawer node itself was replaced.
  function installDrawerObserver() {
    var drawer = document.querySelector(CART_MODAL_SELECTOR);
    if (!drawer || STATE.observedDrawer === drawer) return;

    if (STATE.drawerObserver) STATE.drawerObserver.disconnect();

    STATE.drawerObserver = new MutationObserver(function () {
      if (!smartCartReady()) return;

      var wasOpen = false;
      NATIVE_OPEN_CLASSES.forEach(function (cls) {
        if (drawer.classList.contains(cls)) {
          drawer.classList.remove(cls);
          wasOpen = true;
        }
      });

      if (!wasOpen) return;

      var modal = getCartModal();
      if (modal && typeof modal.hide === "function") modal.hide();
      showSmartCart();
    });

    STATE.drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ["class"],
    });
    STATE.observedDrawer = drawer;
  }

  // Capture-phase fallback for the header cart link. Covers the case where the theme setting
  // `show_modal_cart` is off (no #cart-modal, no funnel) and the link would navigate to /cart.
  function installTriggerInterceptor() {
    document.addEventListener(
      "click",
      function (event) {
        if (!smartCartReady()) return;
        if (event.defaultPrevented) return;
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.button > 0
        )
          return;
        if (!event.target || typeof event.target.closest !== "function") return;

        var trigger = event.target.closest(CART_TRIGGER_SELECTOR);
        if (!trigger || trigger.closest(OPT_OUT_SELECTOR)) return;

        event.preventDefault();
        event.stopPropagation();
        showSmartCart();
      },
      true,
    );
  }

  // Mirrors the theme's own cart badge update (app.js writes the same .js-cart-count nodes) so
  // the header count stays correct after adds/removes made inside Smart Cart or its widgets.
  function syncCartCount() {
    if (
      !window.Rebuy ||
      !window.Rebuy.Cart ||
      typeof window.Rebuy.Cart.itemCount !== "function"
    )
      return;

    var count = window.Rebuy.Cart.itemCount();
    var nodes = document.querySelectorAll(CART_COUNT_SELECTOR);

    Array.prototype.forEach.call(nodes, function (node) {
      node.textContent = count;
    });
  }

  // Everything here is self-guarding, so it is safe to run on every ready — including the
  // re-init ready, which is the point: the funnel is re-checked and the count re-synced.
  function onSmartCartReady() {
    waitForFunnelThenPatch();
    installDrawerObserver();
    syncCartCount();
  }

  // Registered once per page. Re-registering these is what stacks duplicate handlers.
  if (!STATE.listenersInstalled) {
    STATE.listenersInstalled = true;
    installTriggerInterceptor();
    document.addEventListener("rebuy:cart.ready", syncCartCount);
    document.addEventListener("rebuy:cart.change", syncCartCount);
  }

  if (IN_READY_CALLBACK || smartCartReady()) {
    onSmartCartReady();
  }

  // Asset context only: Rebuy is not calling us, so we have to watch for readiness ourselves.
  // Deliberately NOT { once: true } — Smart Cart re-init fires this again, and that refresh is
  // exactly what the admin callback gets for free.
  if (!IN_READY_CALLBACK && !STATE.readyBound) {
    STATE.readyBound = true;
    document.addEventListener("rebuy:smartcart.ready", onSmartCartReady);
  }
})(typeof smartcart !== "undefined" ? smartcart : null);
